// Equal-power fades. A crossfade is one voice going 1 → 0 along a cosine while the other goes
// 0 → 1 along a sine: their powers always add up to 1, so the blend has no dip in loudness.
//
//   gain
//   1 ┤╲        ╱─        out: cos    in: sin
//     │  ╲    ╱           (a linear fade drops 3 dB in the middle)
//   0 ┤────╲╱──────▶ time

/** Points of a full-length fade; the audio graph joins them with linear ramps. */
export const CURVE_POINTS = 64;

const QUARTER_TURN = Math.PI / 2;

/** A fade the engine started: where it began, where it goes, when and for how long (clock seconds). */
export interface Fade {
  from: number;
  to: number;
  start: number;
  seconds: number;
}

/** Shape of progress 0..1 as a rise from 0 to 1. Falling fades use its mirror, so in/out stay paired. */
function rise(progress: number): number {
  return Math.sin(progress * QUARTER_TURN);
}

function fall(progress: number): number {
  return Math.cos(progress * QUARTER_TURN);
}

function level(from: number, to: number, progress: number): number {
  // The ends are exact: cos(π/2) is 6e-17, not 0.
  if (progress <= 0) {
    return from;
  }
  if (progress >= 1) {
    return to;
  }
  if (to >= from) {
    return from + (to - from) * rise(progress);
  }
  return to + (from - to) * fall(progress);
}

/** The gain values for an equal-power fade, evenly spaced in time. */
export function curve(from: number, to: number, count = CURVE_POINTS): Float32Array {
  const points = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    points[i] = level(from, to, i / (count - 1));
  }
  return points;
}

/** Gain of a fade at clock time `now`, so an interrupted fade can continue from where it was. */
export function gainAt(fade: Fade, now: number): number {
  if (fade.seconds <= 0) {
    return fade.to;
  }
  const progress = Math.min(1, Math.max(0, (now - fade.start) / fade.seconds));
  return level(fade.from, fade.to, progress);
}
